/**
 * Building the initial game state from a seed and a player count.
 *
 * Deterministic: `newGame({ seed, playerCount })` always yields the same state.
 * Anything marked R-xxx refers to rules/RULINGS.md.
 */
import { MANIFESTS } from './manifests.js';
import { seedRng, shuffle } from './rng.js';
import { COLORS, TRAY_COUNT } from './types.js';
/**
 * R-001: players arrange their markers freely (setup.arrangeMarkers). This is
 * the layout offered by default — two markers of one colour per tray — which a
 * player may accept as-is.
 */
export function defaultTrays() {
    return COLORS.map((c) => [c, c]);
}
function emptyPlayer(id) {
    const m = MANIFESTS.setup;
    return {
        id,
        trays: defaultTrays(),
        trajanSlots: Array.from({ length: TRAY_COUNT }, () => null),
        arch: 0,
        supply: m.tokensPerPlayer - m.initialWorkers - m.initialLegionnaires,
        workers: m.initialWorkers,
        legionnaires: m.initialLegionnaires,
        leader: 'camp',
        hand: [],
        display: [],
        forumTiles: [],
        extraActionTiles: [],
        constructionTiles: [],
        bonusTiles: [],
        trajanDemandTiles: [],
        plus2Spaces: [],
        vp: 0,
        senate: 0,
    };
}
export function newGame({ seed, playerCount }) {
    if (![2, 3, 4].includes(playerCount))
        throw new Error(`playerCount must be 2–4, got ${playerCount}`);
    const M = MANIFESTS;
    let rng = seedRng(seed);
    // --- shuffle every supply --------------------------------------------------
    const shuffled = (items) => {
        const [out, next] = shuffle(rng, items);
        rng = next;
        return out;
    };
    let drawPile = shuffled(M.tiles.commodity);
    let forumSupply = shuffled(M.tiles.forum);
    let extraActionSupply = shuffled(M.tiles.extraAction);
    let bonusBag = shuffled(M.tiles.bonus);
    const demandSupply = shuffled(M.tiles.demand);
    const constructionOrder = shuffled(M.tiles.construction);
    const trajanStacks = M.tiles.trajanStacks.map((kind) => shuffled(M.tiles.trajan.filter((t) => t.kind === kind)));
    // --- players ---------------------------------------------------------------
    const turnOrder = shuffled(Array.from({ length: playerCount }, (_, i) => i));
    // R-003: the top cards of the deck seed the discard piles, then players draw in player order.
    const seeded = M.setup.discardPilesSeededFromDeck;
    const discards = [drawPile.slice(0, 1), drawPile.slice(1, seeded)];
    drawPile = drawPile.slice(seeded);
    const players = Array.from({ length: playerCount }, (_, i) => emptyPlayer(i));
    for (const id of turnOrder) {
        const p = players[id];
        p.hand = drawPile.slice(0, M.setup.initialHandSize);
        drawPile = drawPile.slice(M.setup.initialHandSize);
        const bonus = bonusBag.slice(0, M.setup.initialBonusTiles);
        bonusBag = bonusBag.slice(M.setup.initialBonusTiles);
        p.bonusTiles = bonus.map((b) => ({ ...b, side: 'yellow' }));
    }
    // --- board -----------------------------------------------------------------
    const provinces = {};
    for (const pr of M.provinces) {
        const tile = forumSupply[0] ?? null;
        forumSupply = forumSupply.slice(1);
        provinces[pr.id] = { tile, legionnaires: [] };
    }
    const forumSpaces = M.setup.forumSpacesByPlayerCount[String(playerCount)];
    const forum = forumSupply.slice(0, forumSpaces);
    forumSupply = forumSupply.slice(forumSpaces);
    const yellowSpaces = extraActionSupply.slice(0, M.setup.yellowSpaces);
    extraActionSupply = extraActionSupply.slice(M.setup.yellowSpaces);
    const senateBonus = [bonusBag[0] ?? null, bonusBag[1] ?? null];
    bonusBag = bonusBag.slice(2);
    // R-031: the district has 4 columns and 3/4/5 rows; leftover tiles are out of the game.
    const siteCount = M.constructionGrid.columns * M.constructionGrid.rowsByPlayerCount[playerCount];
    const board = {
        forum,
        yellowSpaces,
        provinces,
        sites: constructionOrder.slice(0, siteCount).map((tile) => ({ tile, workers: [] })),
        ships: M.ships.map((s) => ({ id: s.id, set: s.set, side: 'colour' })),
        senateBonus,
        trajanStacks,
        // R-027: discs stacked on the start space in player order, first player at the bottom.
        senateStacks: MANIFESTS.senateTrack.spaces.map((_, i) => (i === 0 ? [...turnOrder] : [])),
    };
    // R-009: one round is one lap whose length depends on the player count; the marker starts at 0.
    const start = 0;
    const lap = M.timeTrack.lapByPlayerCount[playerCount];
    return {
        seed,
        rng,
        playerCount,
        players,
        turnOrder,
        current: turnOrder[0],
        // R-001 then R-004: players arrange markers, then pick initial Trajan tiles, in player order.
        phase: { kind: 'setup.arrangeMarkers', player: turnOrder[0] },
        pendingActions: [],
        turn: { performed: [], extraActionUsed: false },
        quarterEnd: null,
        time: {
            position: start,
            start,
            length: lap,
            round: 1,
            quarter: 1,
            crossedStartThisTurn: false,
        },
        demands: [],
        board,
        hidden: { drawPile, forumSupply, extraActionSupply, bonusBag, demandSupply },
        discards,
        removed: {
            trajan: [],
            forum: [],
            extraAction: [],
            demand: [],
            construction: constructionOrder.slice(siteCount),
        },
        quarterTilesRemaining: M.timeTrack.quarters,
        seq: 0,
    };
}
//# sourceMappingURL=setup.js.map